import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { Compass, Loader2, Copy, Check, ExternalLink } from "lucide-react";

/**
 * Mint a guide PWA magic link for a departure (Block 2 B2.6). The plaintext URL is returned
 * ONCE (only its hash is stored) and points at the guide app (guide.ckgo2.com), which
 * works offline. Share it with the trip's guide — no login.
 */
export default function GuideLinkPanel({ tripDateId }: { tripDateId: string }) {
  const { t } = useTranslation();
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mint = useMutation({
    mutationFn: () =>
      api.post<{ data: { url: string; guideName: string | null } }>(
        `/api/admin/departures/${tripDateId}/guide-link`,
        {},
      ),
    onSuccess: (res) => setLink(res.data.url),
  });

  const copy = () => {
    if (!link) return;
    void navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="card">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <Compass className="h-5 w-5" /> {t("ops.guideLink.title")}
      </h2>
      <p className="mb-3 text-xs text-gray-500">{t("ops.guideLink.subtitle")}</p>
      <button
        onClick={() => mint.mutate()}
        disabled={mint.isPending}
        className="btn-ghost btn-sm w-full"
      >
        {mint.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Compass className="h-4 w-4" />
        )}{" "}
        {link ? t("ops.guideLink.regenerate") : t("ops.guideLink.create")}
      </button>
      {link && (
        <div className="mt-2 flex items-center gap-2 rounded bg-gray-50 px-2 py-1.5 dark:bg-gray-800">
          <input
            readOnly
            value={link}
            className="flex-1 bg-transparent font-mono text-xs text-gray-600 dark:text-gray-300"
          />
          <button onClick={copy} className="btn-ghost btn-sm" title={t("ops.guideLink.copyLink")}>
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </button>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost btn-sm"
            title={t("ops.guideLink.openLink")}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}
    </div>
  );
}
